import React, { useCallback, useEffect, useState } from 'react';
import UserCombobox from '../../../util_components/user_combobox/UserCombobox';
import {
  add_sales_member,
  api_error_reason,
  api_status,
  fetch_sales_members,
  remove_sales_member
} from './api';
import type { RoleMembers } from './api';
import { can_remove, display_name, filter_candidates, merge_rows, row_user } from './sales_users';
import type { PanelRow } from './sales_users';
import { fetch_roster } from '../../sales_team/api';
import type { TeamMember, TeamRoster } from '../../sales_team/api';
import {
  format_next_leave,
  format_shift,
  format_weekly_off,
  status_badges
} from '../../sales_team/roster';
import type { BadgeTone } from '../../sales_team/roster';
import EditRepModal from '../../sales_team/EditRepModal';
import HasActiveStudentsNotice from '../../sales_team/HasActiveStudentsNotice';
import StackTd from '../StackTable';
import { stackClasses } from '../stack_table';
import { blocked_by_students } from '../../sales_team/cms_user_fields';
import type { BlockedByStudents } from '../../sales_team/cms_user_fields';
import { Btn, Chip, LABEL_CLASS } from '../ui';
import type { Tone } from '../ui';

export type Notify = (type: 'success' | 'error', message: string) => void;

interface Props {
  notify: Notify;
  self_uuid?: string;
}

const CHIP_TONE: Record<BadgeTone, Tone> = {
  success: 'success',
  warning: 'warning',
  muted: 'neutral'
};

// Weekly off sits under Shift and On shift under Status, so the table fits 1024px with the sidebar.
const COLUMNS = [
  { label: 'Name', width: 'w-[24%]' },
  { label: 'Level', width: 'w-[9%]' },
  { label: 'Shift · weekly off', width: 'w-[20%]' },
  { label: 'Next leave', width: 'w-[16%]' },
  { label: 'Status', width: '' }
];
const CELL = 'px-3.5 py-2.5 text-left text-[13px] font-normal align-top';
const ST = stackClasses();

interface LoadError {
  forbidden: boolean;
  reason: string;
}

function SalesUsersPanel({ notify, self_uuid = '' }: Props) {
  const [roster, set_roster] = useState<TeamRoster | null>(null);
  const [roster_error, set_roster_error] = useState<LoadError | null>(null);
  const [roles, set_roles] = useState<RoleMembers | null>(null);
  // 403 means the caller is not a SUPER_USER: role management is hidden, not an error.
  const [roles_forbidden, set_roles_forbidden] = useState(false);
  const [roles_error, set_roles_error] = useState<string | null>(null);
  const [loading, set_loading] = useState(true);
  const [selected, set_selected] = useState('');
  const [busy, set_busy] = useState<string | null>(null);
  const [editing, set_editing] = useState<TeamMember | null>(null);
  const [blocked, set_blocked] = useState<BlockedByStudents | null>(null);

  const load_roster = useCallback(
    () =>
      fetch_roster()
        .then((r) => {
          set_roster(r);
          set_roster_error(null);
        })
        .catch((e: unknown) => {
          const forbidden = api_status(e) === 403;
          const reason = forbidden
            ? 'Super user only'
            : api_error_reason(e, 'Failed to load sales users');
          set_roster_error({ forbidden, reason });
          if (!forbidden) notify('error', reason);
        }),
    [notify]
  );

  const load_roles = useCallback(
    () =>
      fetch_sales_members()
        .then((d) => {
          set_roles(d);
          set_roles_forbidden(false);
          set_roles_error(null);
        })
        .catch((e: unknown) => {
          if (api_status(e) === 403) {
            set_roles(null);
            set_roles_forbidden(true);
            return;
          }
          const reason = api_error_reason(e, 'Failed to load SALES role members');
          set_roles_error(reason);
          notify('error', reason);
        }),
    [notify]
  );

  const load = useCallback(() => {
    // A refusal notice is only valid for the roster state it was raised on.
    set_blocked(null);
    set_loading(true);
    return Promise.all([load_roster(), load_roles()]).finally(() => set_loading(false));
  }, [load_roster, load_roles]);

  useEffect(() => {
    load();
  }, [load]);

  const can_edit = !!roster?.can_edit;
  const can_manage_roles = !!roles;
  const rows = merge_rows(roster?.members, roles?.members);
  const options = filter_candidates(roles?.candidates, '', roles?.members);
  const chosen = options.find((c) => c.uuid === selected) || null;
  const show_actions = can_edit || can_manage_roles;
  const col_count = COLUMNS.length + (show_actions ? 1 : 0);

  const on_add = () => {
    if (!chosen || busy) return;
    set_busy(chosen.uuid);
    add_sales_member(chosen.uuid)
      .then(() => {
        notify('success', `Added ${display_name(chosen)} to SALES`);
        set_selected('');
        return load();
      })
      .catch((e: unknown) => notify('error', api_error_reason(e, 'Failed to add user')))
      .finally(() => set_busy(null));
  };

  const on_remove = (row: PanelRow) => {
    const u = row_user(row);
    if (busy || !can_remove(u, self_uuid).ok) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove the SALES role from ${display_name(u)}?`)) return;
    set_busy(u.uuid);
    set_blocked(null);
    remove_sales_member(u.uuid)
      .then(() => {
        notify('success', `Removed ${display_name(u)} from SALES`);
        return load();
      })
      .catch((e: unknown) => {
        const refused = blocked_by_students(e, u.uuid);
        if (refused) set_blocked(refused);
        else notify('error', api_error_reason(e, 'Failed to remove user'));
      })
      .finally(() => set_busy(null));
  };

  const on_close = (changed: boolean) => {
    set_editing(null);
    if (changed) load();
  };

  const render_remove = (row: PanelRow) => {
    const name = display_name(row_user(row));
    const check = can_remove(row_user(row), self_uuid);
    return (
      <>
        <span title={check.hint} className="block">
          <Btn
            variant="danger"
            size="row"
            block
            onClick={() => on_remove(row)}
            disabled={!check.ok || !!busy}
            aria-label={`Remove ${name}`}
          >
            {busy === row.uuid ? 'Removing…' : 'Remove'}
          </Btn>
        </span>
        {check.hint ? (
          <div className="mt-1 text-[11.5px] leading-snug text-content-muted">{check.hint}</div>
        ) : null}
      </>
    );
  };

  const render_row = (row: PanelRow) => {
    const m = row.member;
    const name = display_name(row_user(row));
    return (
      <tr
        key={row.uuid}
        className={`border-t border-line-subtle text-[12.5px] ${ST.row} ${
          m?.status === 'LEFT' ? 'bg-surface-row-muted' : ''
        }`}
      >
        <StackTd className={CELL}>
          <span className="break-words font-semibold text-content-primary">{name}</span>
          {row.is_active ? null : (
            <span className="ml-2">
              <Chip tone="outline">Inactive</Chip>
            </span>
          )}
          {row.email && row.email !== name ? (
            <div className="truncate text-[11.5px] text-content-muted" title={row.email}>
              {row.email}
            </div>
          ) : null}
        </StackTd>
        <StackTd label="Level" className={CELL}>
          {m?.level ? <Chip>{m.level}</Chip> : <Chip tone="outline">Not set</Chip>}
        </StackTd>
        <StackTd label="Shift · weekly off" className={CELL}>
          {(() => {
            const shift = format_shift(m?.shift_start, m?.shift_end);
            return shift === '—' ? (
              <div className="text-content-muted">No shift set</div>
            ) : (
              <div className="tabular-nums">{shift}</div>
            );
          })()}
          <div className="text-[11.5px] text-content-muted">
            <span>Weekly off: </span>
            <span>{m ? format_weekly_off(m.weekly_off) : '—'}</span>
          </div>
        </StackTd>
        <StackTd label="Next leave" className={CELL}>
          {(() => {
            const leave = format_next_leave(m?.next_leave);
            return leave === '—' ? <span className="text-content-muted">None planned</span> : leave;
          })()}
        </StackTd>
        <StackTd label="Status" className={CELL}>
          <div className="flex flex-wrap gap-1">
            {m
              ? status_badges(m).map((b) => (
                  <Chip key={b.key} tone={CHIP_TONE[b.tone]}>
                    {b.label}
                  </Chip>
                ))
              : null}
          </div>
          {m?.on_shift_now ? (
            <div className="mt-1 text-[11.5px] font-medium text-state-success-text">On shift</div>
          ) : null}
        </StackTd>
        {show_actions ? (
          <StackTd className={CELL}>
            <div className="flex max-w-[150px] flex-col items-stretch gap-1">
              {can_edit && m ? (
                <Btn
                  variant="neutral"
                  size="row"
                  block
                  onClick={() => set_editing(m)}
                  aria-label={`Edit ${name}`}
                >
                  Edit
                </Btn>
              ) : null}
              {can_manage_roles ? <div>{render_remove(row)}</div> : null}
            </div>
          </StackTd>
        ) : null}
      </tr>
    );
  };

  const render_body = () => {
    if (roster_error && !roster) {
      return (
        <tr className={ST.row}>
          <StackTd colSpan={col_count} className="px-4 py-8 text-center">
            <div role="alert" className="text-[13px]" style={{ color: 'var(--state-error)' }}>
              {roster_error.forbidden
                ? 'Super user only'
                : `Could not load sales users: ${roster_error.reason}`}
            </div>
            {roster_error.forbidden ? null : (
              <Btn
                variant="outline"
                size="row"
                onClick={load}
                disabled={loading}
                style={{ marginTop: 12 }}
              >
                {loading ? 'Retrying…' : 'Retry'}
              </Btn>
            )}
          </StackTd>
        </tr>
      );
    }
    if (loading && !roster) {
      return (
        <tr className={ST.row}>
          <StackTd colSpan={col_count} className="px-4 py-8 text-center text-content-muted">
            Loading sales users…
          </StackTd>
        </tr>
      );
    }
    if (rows.length === 0) {
      return (
        <tr className={ST.row}>
          <StackTd colSpan={col_count} className="px-4 py-8 text-center text-content-muted">
            No users have the SALES role yet.
          </StackTd>
        </tr>
      );
    }
    return rows.map(render_row);
  };

  const render_add_row = () => {
    if (roles) {
      return (
        <div className="flex flex-wrap items-end gap-2">
          <UserCombobox
            id="sales-users-candidate"
            label="User to add"
            users={options}
            value={selected}
            on_change={set_selected}
            disabled={!!busy}
          />
          <Btn variant="primary" onClick={on_add} disabled={!chosen || !!busy}>
            {busy && chosen && busy === chosen.uuid ? 'Adding…' : 'Add user'}
          </Btn>
        </div>
      );
    }
    if (roles_error && !roles_forbidden) {
      return (
        <div className="flex flex-wrap items-center gap-2">
          <span role="alert" className="text-xs" style={{ color: 'var(--state-error)' }}>
            Could not load SALES role members: {roles_error}
          </span>
          <Btn variant="outline" size="row" onClick={load} disabled={loading}>
            {loading ? 'Retrying…' : 'Retry'}
          </Btn>
        </div>
      );
    }
    return null;
  };

  const add_row = render_add_row();
  return (
    <section
      className="overflow-hidden rounded-[10px] border border-line-subtle bg-surface-card text-[13px] text-content-primary"
      style={{ margin: '20px 5%' }}
    >
      <div className="flex flex-wrap items-end gap-x-4 gap-y-2.5 border-b border-line-subtle px-3.5 py-3">
        <div className="min-w-0 flex-1 basis-[320px]">
          <h2 className="m-0 text-[15px] font-semibold">Sales users</h2>
          <p className="m-0 mt-0.5 text-xs text-content-muted">
            Users with the SALES role get sales task pop-ups and can claim tasks.
            {roles ? ' Adding here is the same as adding the SALES role on CMS Users.' : ''}{' '}
            {can_edit
              ? "Use Edit to set a rep's level, shift, weekly off, status and leaves."
              : 'Levels, shifts and leaves are set here by sales managers.'}{' '}
            <span>Shifts in IST</span>
          </p>
        </div>
        {add_row ? <div className="max-sm:w-full">{add_row}</div> : null}
      </div>
      {blocked ? (
        <div className="px-3.5">
          <HasActiveStudentsNotice blocked={blocked} />
        </div>
      ) : null}
      <table className={ST.table}>
        <thead className={`border-b border-line-subtle bg-surface-sunken ${ST.head}`}>
          <tr>
            {COLUMNS.map((c) => (
              <th key={c.label} className={`px-3.5 py-2 text-left ${LABEL_CLASS} ${c.width}`}>
                {c.label}
              </th>
            ))}
            {show_actions ? (
              <th className={`w-[150px] px-3.5 py-2 text-left ${LABEL_CLASS}`}>Actions</th>
            ) : null}
          </tr>
        </thead>
        <tbody className={ST.body}>{render_body()}</tbody>
      </table>
      {editing ? <EditRepModal member={editing} notify={notify} onClose={on_close} /> : null}
    </section>
  );
}

export default SalesUsersPanel;
